import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EventService } from './event.service';
import { EmployeeInfoService } from './employee-info.service';
import { NotificationsService } from './notifications.service';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { UtilityService } from './utility.service';

@Injectable({
  providedIn: 'root',
})
export class BellNotficationService {
  private employeeInfo: any;
  private subscriptions: Subscription[] = [];

  public bellNotifications: Array<{
    BellNotificationID: number;
    Description: string;
    FileName: string;
    DateStarted: any;
    DateCompleted: any;
    Status: number;
    FilePath: string;
  }> = [];
  public hasNewNotification = false;

  constructor(
    private http: HttpClient,
    private eventService: EventService,
    private employeeInfoService: EmployeeInfoService,
    private notificationsService: NotificationsService,
    private toastr: ToastrService,
    private utilityService: UtilityService
  ) {
    this.employeeInfo = this.employeeInfoService.getEmployeeInfo();

    this.subscriptions.push(
      this.eventService.onSendDismissBellNotification((bellNotificationID: number) => this.removeBellNotification(bellNotificationID))
    );
    this.subscriptions.push(
      this.eventService.onSendUpdateBellNotification(
        (
          bellNotificationID: number,
          description: string,
          fileName: string,
          dateStarted: any,
          dateCompleted: any,
          status: number,
          filePath: string
        ) => this.updateBellNotification(bellNotificationID, description, fileName, dateStarted, dateCompleted, status, filePath)
      )
    );
    this.subscriptions.push(this.eventService.onSendRefreshBellNotifications(() => this.getBellNotifications()));

    this.getBellNotifications();
  }

  onDestroy(): void {
    this.subscriptions.forEach(sub => sub?.unsubscribe());
    this.subscriptions = [];
  }

  getBellNotifications() {
    this.getBellNotificationsPromise(this.employeeInfo.UserKeyID)
      .then((response: any) => {
        this.bellNotifications = response;
      })
      .catch(() => {
        this.notificationsService.showError('Failed to get bell notifications', null);
      });
  }

  public hasPendingBellNotification(): boolean {
    return this.bellNotifications.some(bellNotification => bellNotification.Status == 0);
  }

  private getBellNotificationsPromise(userKeyID: number) {
    return this.http.get('/Home/GetBellNotifications?userKeyID=' + userKeyID).toPromise();
  }

  downloadBellNotification(userKeyID: number, bellNotificationID: number, filePath: string) {
    if (filePath == null) {
      // popup error message?
      return;
    }

    this.dismissBellNotification(userKeyID, bellNotificationID)
      .then((response: any) => {
        if (response.success) {
          this.removeBellNotification(bellNotificationID);
          window.open(filePath, '_blank');
        } else {
          // popup error message?
        }
      })
      .catch(() => {
        // popup error message?
      });
  }

  dismissBellNotification(userKeyID: number, bellNotificationID: number) {
    return this.http
      .post('/Home/DismissBellNotification', {
        userKeyID: userKeyID,
        bellNotificationID: bellNotificationID,
      })
      .toPromise();
  }

  public removeBellNotification(bellNotificationID: number) {
    this.bellNotifications = this.bellNotifications.filter(bellNotification => bellNotification.BellNotificationID != bellNotificationID);
  }

  public updateBellNotification(
    bellNotificationID: number,
    description: string,
    fileName: string,
    dateStarted: any,
    dateCompleted: any,
    status: number,
    filePath: string
  ) {
    // try to find bell notification
    const bellNotification = this.bellNotifications.find(bellNotification => bellNotification.BellNotificationID == bellNotificationID);

    // if bell notification found, update it, else add it
    if (bellNotification) {
      bellNotification.Description = description;
      bellNotification.FileName = fileName;
      bellNotification.DateStarted = dateStarted;
      bellNotification.DateCompleted = dateCompleted;
      bellNotification.Status = status;
      bellNotification.FilePath = filePath;
    } else {
      this.bellNotifications.unshift({
        BellNotificationID: bellNotificationID,
        Description: description,
        FileName: fileName,
        DateStarted: dateStarted,
        DateCompleted: dateCompleted,
        Status: status,
        FilePath: filePath,
      });
    }

    this.hasNewNotification = true;

    // show toast message
    this.showToastMessage(description, status);
  }

  private showToastMessage(description: string, status: number): void {
    const configOptions: any = {
      timeOut: 3000,
      disableTimeOut: status == 2 ? true : false,
      progressBar: false,
      closeButton: true,
      tapToDismiss: true,
      positionClass: this.utilityService.isMobileMode() ? 'toast-top-left' : 'toast-top-right',
    };
    const messagePending = 'Report has been queued.';
    const messageComplete = 'Report is ready to be downloaded.';
    const messageError = 'Error generating report.';

    let message: string;
    if (status == 1) {
      message = messageComplete;
    } else if (status == 2) {
      message = messageError;
    } else {
      message = messagePending;
    }

    if (status == 2) {
      this.toastr.error(message, description, configOptions);
    } else {
      this.toastr.success(message, description, configOptions);
    }
  }
}
